import React, { useMemo } from 'react';
import {
  ChartsIcon,
  MercIcon,
  SettingsIcon,
  ReportIcon,
  ExternalLinkIcon,
} from '../../../../theme/icons';
import {
  Badge,
  Box,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AlarmIcon } from '../../../../../../theme/icons';
import {
  ButtonIcon,
  DefaultMenuButtons,
  MenuItemPrimary,
} from '../../../../../../theme/components';
import { getPolyglot } from '../../../../../../i18n';
import { ViewKey } from '../../MerchandiserContainer';
import { RootState, useAppSelector } from '../../../../../../redux/store.model';
import { getAlarmActiveBySelector } from '../../../../../../redux/incident/selectors/getAlarmActiveBySelector';
import { useMerchandiserUIAction } from './MerchandiserUIAction';
import TooltipPrimary from '../../../../../../theme/components/TooltipPrimary';
import { getFeatureToggle } from '../../../../../../featureToggle';

interface MerchandiserMenuButton {
  icon: React.ReactNode;
  view: ViewKey;
  tooltip: string;
  onClick: () => void;
  disabled: boolean;
  showButton?: boolean;
}

const useButtons = ({
  onClick,
  merchandiserId,
}: {
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
}) => {
  const { data: alarmsActive } = useAppSelector((state: RootState) =>
    getAlarmActiveBySelector(state, {
      groupId: merchandiserId,
    })
  );
  const polyglot = getPolyglot();
  const deviceInfo = useMerchandiserUIAction({ merchandiserId });

  return [
    {
      icon: <ExternalLinkIcon />,
      view: 'commanderVPN',
      ...deviceInfo,
    },
    {
      icon: <MercIcon />,
      view: 'detail',
      onClick: () => onClick('detail'),
      tooltip: polyglot.t('freshwatch.dashboard.detail.detaile_title'),
      disabled: false,
      showButton: true,
    },
    {
      icon: <SettingsIcon />,
      view: 'settings',
      tooltip: polyglot.t('device.tooltip.open_window_edit'),
      onClick: () => onClick('settings'),
      showButton: true,
      disabled: false,
    },
    {
      icon: <ChartsIcon />,
      view: 'history',
      onClick: () => onClick('history'),
      tooltip: polyglot.t('device.tooltip.open_window_sensordata'),
      showButton: true,
      disabled: false,
    },
    {
      icon: <ReportIcon />,
      view: 'reports',
      tooltip: polyglot.t('reports.reports'),
      onClick: () => onClick('reports'),
      showButton: Boolean(useAppSelector(getFeatureToggle)?.reports),
      disabled: false,
    },
    {
      icon: (
        <Badge badgeContent={alarmsActive?.length} color="error">
          <AlarmIcon />
        </Badge>
      ),
      view: 'alarms',
      onClick: () => onClick('alarms'),
      tooltip: polyglot.t('incident.all_alarms'),
      showButton: true,
      disabled: false,
    },
  ] as MerchandiserMenuButton[];
};
const useAlarmButton = ({
  onClick,
  merchandiserId,
}: {
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
}) => {
  const { data: alarmsActive } = useAppSelector((state) =>
    getAlarmActiveBySelector(state, {
      groupId: merchandiserId,
    })
  );

  let alarmIcon: JSX.Element | null = (
    <Badge badgeContent={alarmsActive?.length} color="error">
      <AlarmIcon />
    </Badge>
  );

  if (alarmsActive?.length === 0) {
    alarmIcon = null;
  }
  return [
    {
      icon: alarmIcon,
      view: 'alarms',
      onClick: () => onClick('alarms'),
      tooltip: getPolyglot().t('incident.all_alarms'),
      showButton: true,
      disabled: false,
    },
  ] as MerchandiserMenuButton[];
};

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ theme, active }) => `
      background-color: ${
        active
          ? theme.palette.secondary.main
          : theme.palette.primary.contrastText
      };
      border-radius: 1rem;
      padding: 1rem;
      height: 3.5rem;
      margin: 0.5rem;
      width: 4rem;
      &:hover {
        background-color: ${theme.palette.primary.light};
      }
      `
);

interface MerchandiserActionsProps {
  moduleSelected?: string;
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
  diplayJustAlarms?: boolean;
}
function MerchandiserActions(props: Readonly<MerchandiserActionsProps>) {
  const { moduleSelected, onClick, merchandiserId, diplayJustAlarms } = props;

  const alarmButtons = useAlarmButton({ onClick, merchandiserId });
  const allButtons = useButtons({ onClick, merchandiserId });
  const buttons = diplayJustAlarms ? alarmButtons : allButtons;

  const buttonsToDisplay = useMemo(
    () => buttons.filter((button) => button.showButton),
    [buttons]
  );

  const polyglot = getPolyglot();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={() => ({
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'flex-end',
        fontSize: '1.8em',
        color: (theme) => theme.typography.body1.color,
      })}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {isMobile ? (
        <DefaultMenuButtons
          buttons={buttonsToDisplay.map((button) => (
            <MenuItemPrimary
              key={button.view}
              onClick={button.onClick}
              selected={moduleSelected === button.view}
              disabled={button.disabled}
              color={'primary'}
            >
              <ButtonIcon
                variant="text"
                size={'small'}
                endIcon={button.icon}
                tooltip={button.tooltip}
                disabled={button.disabled}
              ></ButtonIcon>
              {polyglot.t(`freshwatch.merchandiser.menu.${button.view}`)}
            </MenuItemPrimary>
          ))}
        ></DefaultMenuButtons>
      ) : (
        buttonsToDisplay.map((button) => {
          return button.icon ? (
            <TooltipPrimary title={button.tooltip} placement="bottom">
              <span>
                <StyledIconButton
                  sx={{ boxShadow: 2 }}
                  key={button.view}
                  active={Boolean(button.view === moduleSelected)}
                  onClick={button.onClick}
                  color="primary"
                  disabled={button.disabled}
                >
                  {button.icon}
                </StyledIconButton>
              </span>
            </TooltipPrimary>
          ) : (
            <div style={{ width: '4rem', margin: '0.5rem' }}></div>
          );
        })
      )}
    </Box>
  );
}

export default MerchandiserActions;
